import { FC, useCallback, useEffect, useState } from 'react';
import {
	Alert,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
} from '@mui/material';
import { api } from 'services/api';
import { getPgErrorMessage } from 'services/api/paymentGateway/errors';
import { TPgWebhookEvent, TPgWebhookEventDetail } from 'services/api/paymentGateway/types';
import { DATE_FILTER } from 'ui/EasTable/constants';
import { EIconButtons } from 'ui/EasTable/EasTableRow/EasTableValue/constants';
import { ESortDirection, ETableStructureType, TTableStructure } from 'ui/EasTable/types';
import { notificationContainer } from 'utils/notificationContainer';
import { PgConfirmDialog } from '../common/PgConfirmDialog';
import { PgFields, PgJsonView } from '../common/PgFields';
import { PgList } from '../common/PgList';
import { PgStatusChip } from '../common/PgMiniTable';
import { useReloadKey } from '../common/helpers';
import { DETAILS_BUTTON, pgActionsColumn, pgColumn, statusColumn } from '../common/tableHelpers';
import { formatDateTime } from '../utils';

type TRow = TPgWebhookEvent;
const col = (
	name: string,
	selector: keyof TRow & string,
	type: ETableStructureType,
	extra: Partial<TTableStructure<keyof TRow, TRow>> = {},
) => pgColumn<TRow>(name, selector, type, extra);

const REPLAY = 'Replay';
const REPLAY_BUTTON = { name: REPLAY, label: 'Replay', icon: EIconButtons.HISTORY };

const STRUCTURE: TTableStructure<keyof TRow, TRow>[] = [
	col('Received', 'receivedAt', ETableStructureType.DATE_TIME, {
		sortable: true,
		filterable: true,
		filter: DATE_FILTER,
	}),
	col('Event', 'name', ETableStructureType.STRING, { sortable: true, filterable: true }),
	col('Status', 'status', ETableStructureType.STATUS, {
		sortable: true,
		filterable: true,
		...statusColumn(['received', 'processing', 'processed', 'ignored', 'failed']),
	}),
	col('Object', 'objectId', ETableStructureType.STRING, { sortable: true, filterable: true }),
	col('Account', 'accountCode', ETableStructureType.STRING),
	col('Attempts', 'attempts', ETableStructureType.STRING, {
		value: (item) => String(item.attempts),
	}),
	col('Processed', 'processedAt', ETableStructureType.DATE_TIME),
	col('Error', 'lastError', ETableStructureType.STRING, {
		tooltip: (item) => item.lastError ?? '',
	}),
	pgActionsColumn<TRow>([DETAILS_BUTTON], (item) =>
		item.status === 'processing' ? [DETAILS_BUTTON] : [DETAILS_BUTTON, REPLAY_BUTTON],
	),
];

/** Webhook events of Airwallex (03 §4.13): the received body and a replay of a stuck or failed event. */
export const WebhooksList: FC = () => {
	const [detailsId, setDetailsId] = useState<number | null>(null);
	const [detail, setDetail] = useState<TPgWebhookEventDetail | null>(null);
	const [detailError, setDetailError] = useState<string | null>(null);
	const [replayRow, setReplayRow] = useState<TRow | null>(null);
	const [replayBusy, setReplayBusy] = useState(false);
	const [reloadKey, reload] = useReloadKey();

	useEffect(() => {
		setDetail(null);
		setDetailError(null);
		if (detailsId === null) return;
		api.paymentGateway
			.getWebhookEvent(detailsId)
			.then(setDetail)
			.catch((e) => setDetailError(getPgErrorMessage(e, 'Failed to load the event')));
	}, [detailsId]);

	const handleAction = useCallback((row: TRow, action: string) => {
		if (action === DETAILS_BUTTON.name) setDetailsId(row.id);
		if (action === REPLAY) setReplayRow(row);
	}, []);

	const replay = async () => {
		if (!replayRow) return;
		setReplayBusy(true);
		try {
			await api.paymentGateway.replayWebhookEvent(replayRow.id);
			notificationContainer('The event is queued for processing again', 'success');
			setReplayRow(null);
			reload();
		} catch (e) {
			notificationContainer(getPgErrorMessage(e, 'Replay failed'), 'error');
		} finally {
			setReplayBusy(false);
		}
	};

	return (
		<>
			<PgList<TRow>
				title="Airwallex webhooks"
				structure={STRUCTURE}
				fetcher={api.paymentGateway.getWebhookEvents}
				onAction={handleAction}
				defaultOrder={{ fieldName: 'receivedAt', direction: ESortDirection.DESC }}
				reloadKey={reloadKey}
				errorText="Failed to load webhook events"
			/>
			<Dialog open={detailsId !== null} onClose={() => setDetailsId(null)} maxWidth="md" fullWidth>
				<DialogTitle color="primary">Webhook event</DialogTitle>
				<DialogContent dividers>
					{!detail && !detailError && <CircularProgress size={24} />}
					{detailError && <Alert severity="error">{detailError}</Alert>}
					{detail && (
						<>
							<PgFields
								items={[
									['Event', detail.name],
									['Status', <PgStatusChip key="s" value={detail.status} />],
									['Event ID', detail.eventId],
									['Object', detail.objectId],
									['Account', detail.accountCode],
									['Created at the provider', formatDateTime(detail.providerCreatedAt)],
									['Received', formatDateTime(detail.receivedAt)],
									['Processed', formatDateTime(detail.processedAt)],
									['Attempts', detail.attempts],
									['Next attempt', formatDateTime(detail.nextAttemptAt)],
									['Last error', detail.lastError],
								]}
							/>
							<PgJsonView value={detail.payload} maxHeight={400} />
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDetailsId(null)}>Close</Button>
				</DialogActions>
			</Dialog>
			<PgConfirmDialog
				open={!!replayRow}
				title="Replay the webhook event"
				confirmLabel="Replay"
				confirmColor="warning"
				busy={replayBusy}
				onConfirm={() => void replay()}
				onClose={() => setReplayRow(null)}
				description={`The event ${replayRow?.name ?? ''} (${replayRow?.eventId ?? ''}) is processed again from the stored body: the status becomes "received" and the attempts start from zero. Handlers are idempotent, but check the payment afterwards.`}
			/>
		</>
	);
};
